import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { Venue } from "shared/assets/icons";
import { Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { StageForm } from "Venues/components";
import { createStage, dataMetaSelector, dispatchGetVenueAction } from "Venues/state";
import { IStage, VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

const venueBlockCopy = copy.venueBlock.stageCapacity;

interface CreateStageModalProps {
  venueId: string;
  handleClose: () => void;
  onCreateSuccess: (stageId: string) => void;
}

export const CreateStageModal = ({
  venueId,
  handleClose,
  onCreateSuccess,
}: CreateStageModalProps) => {
  const dispatch = useDispatch();
  const { api: apiMeta, id: createdStageId } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.createStage)
  );
  useClearActionMetaOnUnMount(VENUE_ACTIONS.createStage);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded && createdStageId) {
      dispatch(
        addTemporaryToast({
          message: venueBlockCopy.messages.stageAdded,
          status: IStatus.success,
        })
      );

      // fetch venue to update
      dispatchGetVenueAction(dispatch, venueId);

      handleClose();

      onCreateSuccess(createdStageId);
    }
  }, [apiMeta?.status, dispatch, handleClose, venueId, createdStageId, onCreateSuccess]);

  const handleSubmit = (stage: IStage) => {
    dispatch(createStage({ venueId, stage }));
  };

  const defaultValues = useMemo(
    () => ({
      stage: createdStageId,
    }),
    [createdStageId]
  );

  const buttons = useMemo(
    (): IFormButtons => ({
      primary: {
        "data-testid": "create_stage",
        children: venueBlockCopy.buttons.createStage,
        size: "m",
      },
      secondary: {
        onClick: handleClose,
        size: "m",
      },
    }),
    [handleClose]
  );

  return (
    <Modal title={venueBlockCopy.titles.createStage} icon={Venue} handleClose={handleClose}>
      <StageForm
        isLightForm
        hasError={!!apiMeta?.error}
        apiStatus={apiMeta?.status}
        handleSubmit={handleSubmit}
        buttons={buttons}
        defaultValues={defaultValues}
      />
    </Modal>
  );
};
